import { useQuery } from '@tanstack/react-query';
import { getNarrationCapabilities, type NarrationCapabilities } from '../../../../features/scenarios/api';
import { NARRATION_STYLE_SUPPORTED, providerSupportsVoiceFilters, type NarrationProviderCode } from '../constants';

export const narrationCapabilitiesQueryKey = (provider: NarrationProviderCode) =>
  ['narration-capabilities', provider] as const;

/** 取得前・取得失敗時の予備値 (従来の provider 単位の定数) */
export function fallbackNarrationCapabilities(provider: NarrationProviderCode): NarrationCapabilities {
  return {
    narration_styles: NARRATION_STYLE_SUPPORTED[provider],
    english_pronunciation: true,
    voice_filters: providerSupportsVoiceFilters(provider),
  };
}

/**
 * プロバイダの今の既定モデル・経路で使えるナレーション機能。
 *
 * Gemini は BE の設定 (3.8 ⇔ 3.1 旧経路) で「読み方」の対応が変わるため BE に問い合わせる。
 * 値はデプロイ (リビジョン) 単位でしか変わらないので長めにキャッシュする。
 */
export function useNarrationCapabilities(
  provider: NarrationProviderCode,
  options?: { enabled?: boolean },
): NarrationCapabilities & { known: boolean } {
  const { data } = useQuery({
    queryKey: narrationCapabilitiesQueryKey(provider),
    queryFn: () => getNarrationCapabilities(provider),
    staleTime: 10 * 60 * 1000,
    retry: 1,
    enabled: options?.enabled ?? true,
  });
  // known=false (取得前・失敗) の間は予備値で表示だけ決め、選択の解除などの副作用には使わない
  return data ? { ...data, known: true } : { ...fallbackNarrationCapabilities(provider), known: false };
}

/**
 * ナレーターの絞り込み (性別・年代) が効くか。Gemini は 3.8 経路で拡張ボイスライブラリの声
 * (性別・年代付き) が一覧に入るときだけ true。capabilities を返さない旧 BE は従来の定数に従う。
 */
export function useVoiceFiltersSupported(provider: NarrationProviderCode | undefined): boolean {
  // provider 未指定 (プロバイダ切替の無い VoiceSelect) は問い合わせず、呼び出し側の判断に任せる
  const capabilities = useNarrationCapabilities(provider ?? 'elevenlabs', { enabled: !!provider });
  if (!provider) return true;
  return capabilities.voice_filters ?? providerSupportsVoiceFilters(provider);
}
